/**
 * サンプル管理ローカル正本（Zip）のセッション作業コピー。
 * ブラウザには永続化しない（IndexedDB に載せるのは FileSystemFileHandle のみ）。
 */

export type SampleLocalStorageRoom = {
  id: number;
  name: string;
  location: string | null;
  type: string | null;
  isShared: boolean;
  createdAt: string;
  updatedAt: string;
};

export type SampleLocalUsageRecord = {
  id: number;
  sampleId: number;
  quantity: number;
  usageDate: string;
  purpose?: string | null;
  notes?: string | null;
  action?: string | null;
  recordedBy?: string | null;
  remainingQuantity?: number | null;
  createdAt: string;
};

export type SampleLocalSample = {
  id: number;
  userId: number;
  storageRoomId?: number | null;
  name: string;
  sampleType?: string | null;
  bslLevel?: string | null;
  infectiousDiseaseLaw?: string | null;
  initialQuantity?: number | null;
  remainingQuantity?: number | null;
  unit?: string | null;
  storageDate?: string | null;
  expirationDate?: string | null;
  notes?: string | null;
  pathogenGenus?: string | null;
  pathogenSpecies?: string | null;
  pathogenSubspecies?: string | null;
  pathogenStrain?: string | null;
  pathogenNote?: string | null;
  livestockInfectiousDiseaseLaw?: string | null;
  drawerNumber?: number | null;
  drawerNote?: string | null;
  caseName?: string | null;
  lastUsedDate?: string | null;
  disposalDate?: string | null;
  creatorDisplayName?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type SampleLocalMeta = {
  revision: number;
  updatedAt: string;
  /** 旧 Zip 互換（表示には未使用） */
  labLabel?: string;
};

export type SampleLocalSnapshot = {
  version: 1;
  meta: SampleLocalMeta;
  storageRooms: SampleLocalStorageRoom[];
  samples: SampleLocalSample[];
  usageRecords: SampleLocalUsageRecord[];
  counters: {
    storageRoom: number;
    sample: number;
    usage: number;
  };
};

const LOCAL_USER_ID = 0;

export function emptySampleSnapshot(): SampleLocalSnapshot {
  const now = new Date().toISOString();
  return {
    version: 1,
    meta: { revision: 1, updatedAt: now },
    storageRooms: [],
    samples: [],
    usageRecords: [],
    counters: { storageRoom: 1, sample: 1, usage: 1 },
  };
}

export function normalizeSampleLocalSnapshot(raw: unknown): SampleLocalSnapshot {
  const base = emptySampleSnapshot();
  if (!raw || typeof raw !== 'object') return base;
  const o = raw as Partial<SampleLocalSnapshot>;
  const rooms =
    Array.isArray(o.storageRooms) && o.storageRooms.length > 0 ? o.storageRooms : base.storageRooms;
  const samples = Array.isArray(o.samples) ? o.samples : [];
  const usageRecords = Array.isArray(o.usageRecords) ? o.usageRecords : [];
  const nextId = (rows: { id?: number }[], fallback: number) =>
    Math.max(fallback, ...rows.map((r) => Number(r?.id) + 1).filter((n) => Number.isFinite(n)));
  return {
    version: 1,
    meta: {
      revision: Math.max(1, Number(o.meta?.revision) || 1),
      updatedAt: o.meta?.updatedAt || base.meta.updatedAt,
      labLabel: o.meta?.labLabel,
    },
    storageRooms: rooms,
    samples,
    usageRecords,
    counters: {
      storageRoom: Math.max(
        1,
        Number(o.counters?.storageRoom) || base.counters.storageRoom,
        nextId(rooms, 1)
      ),
      sample: Math.max(1, Number(o.counters?.sample) || 1, nextId(samples, 1)),
      usage: Math.max(1, Number(o.counters?.usage) || 1, nextId(usageRecords, 1)),
    },
  };
}

let sessionSnapshot: SampleLocalSnapshot | null = null;
let writtenHook: (() => void) | null = null;

export function setSampleSnapshotWrittenHook(hook: (() => void) | null): void {
  writtenHook = hook;
}

export function isSampleLocalSnapshotLoaded(): boolean {
  return sessionSnapshot != null;
}

export function resetSampleLocalSession(): void {
  sessionSnapshot = null;
}

export function getSampleLocalSnapshot(): SampleLocalSnapshot {
  if (!sessionSnapshot) {
    throw new Error('sample.local_snapshot_not_loaded');
  }
  return sessionSnapshot;
}

export function getSampleLocalSnapshotOrNull(): SampleLocalSnapshot | null {
  return sessionSnapshot;
}

export function replaceSampleLocalSnapshot(next: SampleLocalSnapshot, opts?: { silent?: boolean }): void {
  sessionSnapshot = normalizeSampleLocalSnapshot(next);
  if (!opts?.silent) {
    writtenHook?.();
  }
}

export function mutateSampleLocalSnapshot(
  mutator: (snap: SampleLocalSnapshot) => void,
  opts?: { silent?: boolean }
): SampleLocalSnapshot {
  const snap = structuredClone(getSampleLocalSnapshot());
  mutator(snap);
  snap.meta.revision = (snap.meta.revision || 0) + 1;
  snap.meta.updatedAt = new Date().toISOString();
  replaceSampleLocalSnapshot(snap, opts);
  return getSampleLocalSnapshot();
}

export function sampleLocalGuestUserId(): number {
  return LOCAL_USER_ID;
}
